import React, { useEffect, useState } from 'react';

import formatXML from 'xml-formatter';
import dayjs from '~/timeInit';

import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Pagination,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import { Code } from '~/components/Common/Code';

import isJSON from '~/utils/isJSON';

interface AriLog {
  id: string;
  createdAt: string;
  messageName: string;
  originalRequest: string;
  hotelCode: string;
  startDate: string;
  endDate: string;
  propertyId: string;
}

interface AriLogsProps {
  ariLogs: Array<AriLog> | null | 'NOT_SUPPORTED';
  date: string | null;
  onHide: () => void;
}

const formatContent = (content: string): string => {
  if (isJSON(content)) {
    try {
      return JSON.stringify(JSON.parse(content), null, 2);
    } catch (e) {
      return content;
    }
  }

  try {
    return formatXML(content, { indentation: '  ' });
  } catch (e) {
    return content;
  }
};

export default function AriLogs({ ariLogs, date, onHide }: AriLogsProps) {
  const [activePage, setActivePage] = useState(1);
  const logsPerPage = 5;

  useEffect(() => {
    setActivePage(1);
  }, [date]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setActivePage(page);
  };

  const paginatedLogs = Array.isArray(ariLogs)
    ? ariLogs.slice((activePage - 1) * logsPerPage, activePage * logsPerPage)
    : [];

  return (
    <Dialog maxWidth="lg" fullWidth open={date !== null} onClose={onHide}>
      <DialogTitle
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          py: 2,
        }}
        variant="h6"
      >
        Ari Logs: {date}
      </DialogTitle>
      <DialogContent>
        {ariLogs === null && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {ariLogs !== null && Array.isArray(ariLogs) && ariLogs.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No Logs Found
          </Alert>
        )}

        {ariLogs === 'NOT_SUPPORTED' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This channel manager does not support ARI logs.
          </Alert>
        )}

        {Array.isArray(ariLogs) && ariLogs.length > 0 && (
          <Stack spacing={3} sx={{ mt: 2 }}>
            {paginatedLogs.map((ariLog, index) => (
              <Paper
                key={ariLog.id || index}
                elevation={2}
                sx={{
                  p: 3,
                  borderLeft: 3,
                  borderColor: 'primary.main',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {dayjs(ariLog.createdAt).format('MMMM Do YYYY, h:mm:ss a')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {dayjs(ariLog.startDate).format('DD MMM YYYY')} - {dayjs(ariLog.endDate).format('DD MMM YYYY')}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" fontWeight="medium">
                      {ariLog.messageName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Hotel Code: {ariLog.hotelCode}
                    </Typography>
                  </Box>
                </Stack>

                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase' }}>
                  Original Request
                </Typography>

                <Code
                  value={formatContent(ariLog.originalRequest)}
                  lang={isJSON(ariLog.originalRequest) ? 'json' : 'xml'}
                />
              </Paper>
            ))}
          </Stack>
        )}
      </DialogContent>
      {Array.isArray(ariLogs) && ariLogs.length > logsPerPage && (
        <DialogActions
          sx={{
            justifyContent: 'center',
            borderTop: 1,
            borderColor: 'divider',
            py: 2,
          }}
        >
          <Pagination
            count={Math.ceil(ariLogs.length / logsPerPage)}
            page={activePage}
            onChange={handlePageChange}
            color="primary"
          />
        </DialogActions>
      )}
    </Dialog>
  );
}
